import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { useBrand } from "@/config/brands";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { UserMenu } from "@/components/UserMenu";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Index from "./pages/Index";
import HomeBeta from "./pages/HomeBeta";
import NearMe from "./pages/NearMe";
import { RouteErrorBoundary } from "./components/RouteErrorBoundary";
import SearchEnhanced from "./pages/SearchEnhanced";
import HospitalProfile from "./pages/HospitalProfile";
import QualityMeasureLeaderboard from "./pages/QualityMeasureLeaderboard";
import HospitalMap from "./pages/HospitalMap";
import Admin from "./pages/Admin";
import AdminEmailTemplates from "./pages/AdminEmailTemplates";
import ProcedureProfile from "./pages/ProcedureProfile";
import { ProcedureCodePage } from "./pages/CPTCodes";
import Auth from "./pages/Auth";
import Donate from "./pages/Donate";
import Merch from "./pages/Merch";
import Settings from "./pages/Settings";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import DataMethodology from "./pages/DataMethodology";
import ConnectToCare from "./pages/ConnectToCare";
import FollowedHospitals from "./pages/FollowedHospitals";
import InternationalSectionRail from "./components/InternationalSectionRail";
import ReleaseNotes from "./pages/ReleaseNotes";
import DataSources from "./pages/DataSources";
import HospitalRankings from "./pages/HospitalRankings";
import APIAccess from "./pages/APIAccess";
import ApiAccessForm from "./pages/ApiAccessForm";
// ApiDocs pulls in the full @scalar/api-reference-react bundle (~3MB gzipped).
// Lazy-load it so the main app bundle stays slim — only visitors who land on
// /api/docs pay the cost.
import { lazy, Suspense } from "react";
const ApiDocs = lazy(() => import("./pages/ApiDocs"));
import MCPAccess from "./pages/MCPAccess";
import UnitNormalization from "./pages/UnitNormalization";
import CommonProcedures from "./pages/CommonProcedures";
import WorstHospitalOffenders from "./pages/WorstHospitalOffenders";
import HospitalIngestionDashboard from "./pages/HospitalIngestionDashboard";
import StateIngestionDashboard from "./pages/StateIngestionDashboard";
import TranslationDashboard from "./pages/TranslationDashboard";
import Scenarios from "./pages/Scenarios";
import FamilyPlanner from "./pages/FamilyPlanner";
import CarePlan from "./pages/CarePlan";
import CheckoutSuccess from "./pages/CheckoutSuccess";
const PricePlanBuilder = lazy(() => import("./pages/PricePlanBuilder"));
import CommunityStories from "./pages/CommunityStories";
import InternationalComparison from "./pages/InternationalComparison";
import InternationalHub from "./pages/InternationalHub";
import InternationalScenarioCalculator from "./pages/InternationalScenarioCalculator";
import HundredDollarChallenge from "./pages/HundredDollarChallenge";
import InternationalCodeExplorer from "./pages/InternationalCodeExplorer";
import InternationalHeatmap from "./pages/InternationalHeatmap";
import InternationalViralCards from "./pages/InternationalViralCards";
import CountryDetail from "./pages/CountryDetail";
import LawsuitsAgainstUs from "./pages/LawsuitsAgainstUs";
import Blog from "./pages/Blog";
import Follow from "./pages/Follow";
import ForBusiness from "./pages/ForBusiness";
import Contact from "./pages/Contact";
import AboutDignity from "./pages/AboutDignity";
import StateComparison from "./pages/StateComparison";
import PriceInvestigator from "./pages/PriceInvestigator";
import DignityChat from "./pages/DignityChat";
import NotFound from "./pages/NotFound";
import { BrandTheme } from "@/components/BrandTheme";
import { AppFooter } from "@/components/AppFooter";
import { NativeTabBar } from "@/components/NativeTabBar";
import { CookieConsent } from "@/components/CookieConsent";
import { isNativeApp } from "@/lib/platform";
import { LanguageRouter } from "@/components/LanguageRouter";
import { ContextMenu } from "@/components/ContextMenu";
import { DarkModeToggle } from "@/components/DarkModeToggle";
import { LanguageSelector } from "@/components/LanguageSelector";
import { AhpGlobalHeader } from "@/components/AhpGlobalHeader";
import { PatrioticScrollbar } from "@/components/PatrioticScrollbar";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { GlobalFlagBackgroundProvider } from "@/components/GlobalFlagBackground";
import React, { useState } from "react";
import '@/i18n';
import { usePageTracking } from "@/hooks/usePageTracking";
import { useAppReviewFlags } from "@/config/appReviewFlags";
import { useAdminStatus } from "@/hooks/useAdminStatus";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 1h staleness (was Infinity): PWA/standalone sessions live for days —
      // with Infinity + refetchOnMount:false a returning session NEVER saw
      // fresh data even after server-side fixes. refetchOnMount respects
      // staleTime, so this does not cause request storms within the hour.
      staleTime: 60 * 60 * 1000,
      gcTime: Infinity,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
    },
  },
});

const UserMenuWrapper = () => {
  return null;
};

/** Scroll to top on route change (SPA navigation preserves scroll position otherwise) */
function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

/** /stories gated behind the show_stories remote flag: hidden everywhere
 *  until the community section is seeded with real content (an empty wall
 *  reads as "no users"). Admins bypass so they can seed it. */
function GatedStories() {
  const { showStories } = useAppReviewFlags();
  const { data: adminStatus } = useAdminStatus();
  if (!showStories && !adminStatus?.isAdmin) return <Navigate to="/" replace />;
  return <CommunityStories />;
}

/** /release-notes gated behind the app-review remote flag: hidden on the
 *  native iOS build until App Store approval (Apple rejects "beta"/changelog
 *  surfaces), then flipped on remotely. Always visible on web. */
function GatedReleaseNotes() {
  const brand = useBrand();
  const { showReleaseNotes } = useAppReviewFlags();
  if (!showReleaseNotes) return <Navigate to="/" replace />;
  // AHP-only. ReleaseNotes queries release_notes with a hardcoded
  // project_slug = 'americanhealthprices', and that is the only slug with any
  // rows — so under Luigi's or AntiGreed this rendered AHP's entire public
  // changelog (101 entries, 9 of them naming Dignity) as if it were theirs.
  // Filtering by brand would just yield an empty page, so gate the route.
  if (brand.key !== 'ahp') return <Navigate to="/" replace />;
  return <ReleaseNotes />;
}

/** /donate gated behind the show_payments remote flag: hidden on the native
 *  iOS build (web-payment surfaces aren't allowed without IAP), always
 *  visible on web. */
function GatedDonate() {
  const { showPayments } = useAppReviewFlags();
  if (!showPayments) return <Navigate to="/" replace />;
  return <Donate />;
}

/** /chat is the public "Connect your AI" guide (Master 2026-07-13) — the
 *  on-site LLM chat is intentionally disconnected, so the page teaches users
 *  to connect their own Claude/ChatGPT to the AHP MCP server instead. No
 *  gate: it's a static setup FAQ, not a chat. */
function GatedChat() {
  return <DignityChat />;
}

/** /about/dignity is Dignity's own origin-story page — her name, her portrait,
 *  her voice, seven times over. She is AHP's mascot, so on Luigi's and AntiGreed
 *  the page is nothing but another brand's character; send those visitors home
 *  rather than rebranding a mascot that isn't theirs. */
function DignityRoute() {
  const brand = useBrand();
  if (brand.key !== 'ahp') return <Navigate to="/" replace />;
  return <AboutDignity />;
}

/** /merch gated like /stories: hidden until the remote flag opens the store
 *  publicly; admins bypass so it can be QA'd first. */
function GatedMerch() {
  const { showMerch } = useAppReviewFlags();
  const { data: adminStatus } = useAdminStatus();
  if (!showMerch && !adminStatus?.isAdmin) return <Navigate to="/" replace />;
  return <Merch />;
}

/** Forward old /hospital-rankings-beta/:state[/:rankType] URLs to /hospital-rankings/...
 *  Preserves any query string + hash. */
function RedirectFromBeta() {
  const { state, rankType } = useParams<{ state: string; rankType?: string }>();
  const { search, hash } = useLocation();
  const target = `/hospital-rankings/${state}${rankType ? `/${rankType}` : ''}${search}${hash}`;
  return <Navigate to={target} replace />;
}

function RedirectFromBetaExecComp() {
  const { state } = useParams<{ state: string }>();
  const { search, hash } = useLocation();
  return <Navigate to={`/hospital-rankings/executive-comp/${state}${search}${hash}`} replace />;
}

/* The "/" landing surface.
 *
 * Native app  → Search, as before.
 * Phone web   → Search too. Landing on the desktop homepage from a phone reads
 *               as a marketing page you have to scroll past; the native default
 *               (open straight into search) fits the small screen far better.
 * Desktop web → HomeBeta, unchanged.
 *
 * Phone web renders Search AT "/" rather than redirecting to /search. A
 * redirect would be read by Google as the homepage bouncing elsewhere — the
 * crawler is mobile-first, so it would take the phone path — and "/" is the
 * most linked, highest-authority URL on the site. Same URL, no redirect, and
 * the homepage's own SEO tags are emitted below so "/" keeps its title,
 * description and self-canonical regardless of which body is shown.
 * ConsumerSearch (what the AHP brand renders) sets no Helmet of its own, so
 * there is nothing to collide with.
 *
 * The phone check is resolved once, synchronously, in a useState initialiser:
 * useIsMobile() starts undefined and settles in an effect, which would flash
 * the desktop homepage first. Not reactive to resize on purpose — rotating a
 * phone into landscape crosses 768px and would otherwise swap the page out
 * mid-session.
 */
function HomeRoute() {
  const brand = useBrand();
  const [isPhone] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches);

  if (isNativeApp()) return <Navigate to="/search" replace />;

  // HomeBeta is AHP's homepage specifically — flag imagery, crimson/gold, and
  // copy written for AHP. It carries 4 brand references; Index carries 17 and
  // renders brand.messaging (heroTitle, heroSubtitle, corruptionTitle, …), which
  // is how each sibling brand states its own identity: "Anti / Greed" on cream
  // and dark green, "Luigi's / Hall of Shame" on green and red.
  //
  // Every non-AHP brand therefore belongs on Index. Pointing them at HomeBeta
  // made both antigreed.app and luigishallofshame.com render AHP's homepage
  // verbatim — h1 "What hospitals actually charge for your care", with the word
  // "Luigi" appearing nowhere on Luigi's own front page.
  if (brand.key !== 'ahp') return <Index />;

  // Phone-web opens on Search for AHP only. Scoped deliberately: the sibling
  // brands have their own homepage above and should not inherit AHP's mobile
  // behaviour just because they share this route.
  if (!isPhone) return <HomeBeta />;

  return (
    <>
      <Helmet>
        <title>{brand.seo.title}</title>
        <meta name="description" content="Compare real hospital prices across 9,500+ hospitals (4,800+ ranked A-F). 555M+ pricing records. 11,900+ medical scenarios. Search by procedure, ZIP code, or hospital name." />
        <link rel="canonical" href={`${brand.primaryDomain}/`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${brand.primaryDomain}/`} />
        <meta property="og:title" content={brand.seo.title} />
        <meta property="og:description" content="Compare real hospital prices across 9,500+ hospitals. 555M+ pricing records from hospitals' own published chargemasters." />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <SearchEnhanced />
    </>
  );
}

const AppContent = () => {
  usePageTracking();
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);

  // Embed mode: when the URL has `?embed=1`, hide the global header, footer,
  // and floating UI chrome so the page can be iframed cleanly (e.g. from the
  // /home-beta "Ten dimensions" section embedding /hospital-rankings).
  const { search, pathname } = useLocation();
  const isEmbed = new URLSearchParams(search).get('embed') === '1';
  // Reset the route error boundary whenever the top-level section changes, so
  // tapping a different tab always recovers from a page error.
  const sectionKey = pathname.split('/')[1] || 'home';

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  };

  return (
    <div onContextMenu={handleContextMenu} className="min-h-screen">
      <ScrollToTop />
      <BrandTheme />
      {!isEmbed && <AhpGlobalHeader />}
      {!isEmbed && <InternationalSectionRail />}
      <PatrioticScrollbar />
      {!isEmbed && <DarkModeToggle />}
      {!isEmbed && <LanguageSelector />}
      {!isEmbed && <UserMenuWrapper />}

      <RouteErrorBoundary key={sectionKey}>
      <Routes>
        {/* The app has no marketing homepage — it opens into Search (a
            location-aware "Near Me" landing will replace this). Desktop web
            keeps HomeBeta; phones get Search, matching the native app. */}
        <Route path="/" element={<HomeRoute />} />
        {/* Old /home-beta path 301s in middleware; Navigate here covers in-SPA links */}
        <Route path="/home-beta" element={<Navigate to="/" replace />} />
        <Route path="/search" element={<SearchEnhanced />} />
        <Route path="/near-me" element={<NearMe />} />
        <Route path="/hospital/:id" element={<HospitalProfile />} />
        <Route path="/hospital/:id/:slug" element={<HospitalProfile />} />
        <Route path="/hospital/:id/chargemasters" element={<HospitalProfile />} />
        {/* Tab-specific deep-links for SEO + share-friendly URLs. Each path maps to a specific tab
            in HospitalProfile via the location.pathname check in the tab-detection useEffect. */}
        <Route path="/hospital/:id/financial" element={<HospitalProfile />} />
        <Route path="/hospital/:id/quality" element={<HospitalProfile />} />
        <Route path="/hospital/:id/scenarios" element={<HospitalProfile />} />
        {/* BETA — alias for the existing per-hospital page. Use as a sandbox for layout tweaks. noindex. */}
        <Route path="/hospital-beta/:id" element={<HospitalProfile />} />
        <Route path="/hospital-beta/:id/:slug" element={<HospitalProfile />} />
        <Route path="/procedure/:code" element={<ProcedureProfile />} />
        <Route path="/map" element={<HospitalMap />} />
        <Route path="/map/:stateOrHospital" element={<HospitalMap />} />
        <Route path="/map/:state/:cityOrHospital" element={<HospitalMap />} />
        <Route path="/cpt" element={<ProcedureCodePage codeType="cpt" />} />
        <Route path="/cpt/:code" element={<ProcedureCodePage codeType="cpt" />} />
        <Route path="/drg" element={<ProcedureCodePage codeType="drg" />} />
        <Route path="/drg/:code" element={<ProcedureCodePage codeType="drg" />} />
        <Route path="/hcpcs" element={<ProcedureCodePage codeType="hcpcs" />} />
        <Route path="/hcpcs/:code" element={<ProcedureCodePage codeType="hcpcs" />} />
        <Route path="/revenue" element={<ProcedureCodePage codeType="revenue" />} />
        <Route path="/revenue/:code" element={<ProcedureCodePage codeType="revenue" />} />
        <Route path="/rev" element={<ProcedureCodePage codeType="revenue" />} />
        <Route path="/rev/:code" element={<ProcedureCodePage codeType="revenue" />} />
        <Route path="/drugs" element={<ProcedureCodePage codeType="drug" />} />
        <Route path="/drugs/:code" element={<ProcedureCodePage codeType="drug" />} />
        <Route path="/ndc" element={<ProcedureCodePage codeType="ndc" />} />
        <Route path="/ndc/:code" element={<ProcedureCodePage codeType="ndc" />} />
        <Route path="/supply" element={<ProcedureCodePage codeType="supply" />} />
        <Route path="/supply/:code" element={<ProcedureCodePage codeType="supply" />} />
        <Route path="/admin" element={
          <ProtectedRoute requireAdmin>
            <Admin />
          </ProtectedRoute>
        } />
        <Route path="/admin/email-templates" element={
          <ProtectedRoute requireAdmin>
            <AdminEmailTemplates />
          </ProtectedRoute>
        } />
        {/* Login isn't surfaced on native, and magic-link redirects break in
            the webview — send the native app back to home. */}
        <Route path="/auth" element={isNativeApp() ? <Navigate to="/" replace /> : <Auth />} />
        <Route path="/donate" element={<GatedDonate />} />
        <Route path="/merch" element={<GatedMerch />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/data-methodology" element={<DataMethodology />} />
        <Route path="/release-notes" element={<GatedReleaseNotes />} />
        <Route path="/connect-to-care" element={<ConnectToCare />} />
        <Route path="/data-sources" element={<DataSources />} />
        <Route path="/api" element={<APIAccess />} />
        <Route path="/api/access" element={<ApiAccessForm />} />
        <Route
          path="/api/docs"
          element={
            <Suspense fallback={<div style={{ minHeight: "70vh" }} />}>
              <ApiDocs />
            </Suspense>
          }
        />
        <Route path="/mcp" element={<MCPAccess />} />
        <Route path="/hospital-rankings" element={<HospitalRankings />} />
        <Route path="/hospital-rankings/:state" element={<HospitalRankings />} />
        <Route path="/hospital-rankings/:state/:rankType" element={<HospitalRankings />} />
        {/* Sort-mode routes — same page, different default sortBy. The component reads
            stateParam/rankTypeParam = 'executive-comp' and overrides sortBy to
            'exec_comp_per_bed'. Hospitals without 990 exec data sort to the bottom. */}
        <Route path="/hospital-rankings/executive-comp" element={<HospitalRankings />} />
        <Route path="/hospital-rankings/executive-comp/:state" element={<HospitalRankings />} />
        {/* Financial leaderboards — SEO-friendly URLs per dimension. The component
            reads :dimension and applies the matching sort (exec-bed, operating-margin,
            charity-care, revenue, etc.) plus dimension-aware title/description meta.
            Canonical URL for exec-comp moves here from /executive-comp/. */}
        <Route path="/hospital-rankings/financial" element={<HospitalRankings />} />
        <Route path="/hospital-rankings/financial/:dimension" element={<HospitalRankings />} />
        <Route path="/hospital-rankings/financial/:dimension/:state" element={<HospitalRankings />} />
        {/* Old beta URLs — redirect to canonical /hospital-rankings/* path. Keeps
            external links + bookmarks working without exposing a "beta" route in the UI. */}
        <Route path="/hospital-rankings-beta" element={<Navigate to="/hospital-rankings" replace />} />
        <Route path="/hospital-rankings-beta/:state" element={<RedirectFromBeta />} />
        <Route path="/hospital-rankings-beta/:state/:rankType" element={<RedirectFromBeta />} />
        <Route path="/hospital-rankings-beta/executive-comp" element={<Navigate to="/hospital-rankings/executive-comp" replace />} />
        <Route path="/hospital-rankings-beta/executive-comp/:state" element={<RedirectFromBetaExecComp />} />
        {/* Per-measure leaderboard — every hospital that reports a CMS measure, sortable. */}
        <Route path="/quality/measure/:measureId" element={<QualityMeasureLeaderboard />} />
        <Route path="/worst-hospital-offenders" element={<WorstHospitalOffenders />} />
        {/* /extreme-price-gouging retired 2026-07-13 at Master's direction —
            the by-name single-procedure leaderboard isn't a surface we want
            public. Old links land on the hospital-level rankings instead. */}
        <Route path="/extreme-price-gouging" element={<Navigate to="/hospital-rankings" replace />} />
        <Route path="/unit-normalization" element={
          <ProtectedRoute>
            <UnitNormalization />
          </ProtectedRoute>
        } />
        <Route path="/common-procedures" element={<CommonProcedures />} />
        <Route path="/scenarios" element={<Scenarios />} />
        <Route path="/scenarios/:uuid/:slug" element={<Scenarios />} />
        <Route path="/scenarios/:uuid/:slug/hospital/:hospitalId" element={<Scenarios />} />
        <Route path="/scenarios/:uuid/:slug/severity/:level" element={<Scenarios />} />
        <Route path="/scenarios/:uuid/:slug/:state/severity/:level" element={<Scenarios />} />
        <Route path="/scenarios/:uuid/:slug/:state" element={<Scenarios />} />
        <Route path="/chat" element={<GatedChat />} />
        <Route path="/family" element={<FamilyPlanner />} />
        <Route path="/followed" element={<FollowedHospitals />} />
        <Route path="/price-plan" element={<PricePlanBuilder />} />
        <Route path="/plan/new" element={<CarePlan />} />
        <Route path="/plan/checkout-success" element={<CheckoutSuccess />} />
        <Route path="/plan/:planId" element={<CarePlan />} />
        <Route path="/stories" element={<GatedStories />} />
        <Route path="/international" element={<InternationalHub />} />
        <Route path="/international/detailed" element={<InternationalComparison />} />
        <Route path="/international/calculator" element={<InternationalScenarioCalculator />} />
        <Route path="/international/calculator/:uuid/:slug" element={<InternationalScenarioCalculator />} />
        <Route path="/international/100-dollar-challenge" element={<HundredDollarChallenge />} />
        <Route path="/international/code-explorer" element={<InternationalCodeExplorer />} />
        <Route path="/international/code-explorer/:code" element={<InternationalCodeExplorer />} />
        <Route path="/international/heatmap" element={<InternationalHeatmap />} />
        <Route path="/international/share" element={<InternationalViralCards />} />
        <Route path="/international/:countrySlug" element={<CountryDetail />} />
        <Route path="/ingestion-dashboard" element={
          <ProtectedRoute requireAdmin>
            <HospitalIngestionDashboard />
          </ProtectedRoute>
        } />
        <Route path="/translation-dashboard" element={
          <ProtectedRoute requireAdmin>
            <TranslationDashboard />
          </ProtectedRoute>
        } />
        <Route path="/ingestion-dashboard/state/:stateCode" element={
          <ProtectedRoute requireAdmin>
            <StateIngestionDashboard />
          </ProtectedRoute>
        } />
        <Route path="/lawsuits" element={<LawsuitsAgainstUs />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/state/:stateCode" element={<Blog />} />
        <Route path="/blog/:slug" element={<Blog />} />
        <Route path="/follow" element={<Follow />} />
        <Route path="/for-business" element={<ForBusiness />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/about/dignity" element={<DignityRoute />} />
        <Route path="/state-comparison" element={<StateComparison />} />
        <Route path="/watchdog" element={<PriceInvestigator />} />
        <Route path="/watchdog/:state" element={<PriceInvestigator />} />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </RouteErrorBoundary>
      {!isEmbed && (isNativeApp() ? <NativeTabBar /> : <AppFooter />)}
      {/* Native app uses iOS/Android OS privacy controls + App Store privacy
          labels — not the web cookie-consent banner. */}
      {!isEmbed && !isNativeApp() && <CookieConsent />}
      {!isEmbed && <Toaster />}
      {!isEmbed && <Sonner />}

      {/* Custom Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
        />
      )}
    </div>
  );
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      {/* v7_startTransition wraps router updates in React.startTransition so
          an imperative navigate() to a lazy route can't suspend a hydrated
          boundary mid-update (React #426 -> route error boundary). */}
      <BrowserRouter future={{ v7_startTransition: true }}>
        <ThemeProvider>
          <TooltipProvider>
            <GlobalFlagBackgroundProvider>
              <AppContent />
            </GlobalFlagBackgroundProvider>
          </TooltipProvider>
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
